import { codigoConcesionUsuario } from "../infoConcesion/scope";
import axiosInstance from "core/api/axiosInstance";

// Contrato propuesto en README: no se hacen peticiones hasta configurar el backend.
const baseURL = () => {
  const url = process.env.REACT_APP_INFO_CONCESION_API_URL?.trim();
  if (!url) {
    throw new Error(
      "Info Concesión está pendiente de conexión al backend. Configura REACT_APP_INFO_CONCESION_API_URL cuando estén disponibles los endpoints."
    );
  }
  return url.replace(/\/+$/, "");
};
export const infoConcesionConfigurada = () =>
  Boolean(process.env.REACT_APP_INFO_CONCESION_API_URL?.trim());

function obtenerDatos(response) {
  const body = response.data;
  if (!body || body.success === false)
    throw new Error(body?.message || "El servidor rechazó la operación.");
  return body.data;
}

export async function getAsignacionInfoConcesion(signal, code) {
  const data = obtenerDatos(
    await axiosInstance.get(`${baseURL()}/asignacion`, {
      signal,
      timeout: 15000,
      ...(code ? { params: { code } } : {}),
    })
  );
  if (
    !data?.concesion?.id ||
    !data.concesion.nombre ||
    !Array.isArray(data.peajes) ||
    data.peajes.some((p) => !p?.id || !p.nombre)
  ) {
    throw new Error("La respuesta de la concesión asignada tiene un formato inválido.");
  }
  return data;
}

export async function getLiquidacionesInfoConcesion(signal, code) {
  const data = obtenerDatos(
    await axiosInstance.get(`${baseURL()}/liquidaciones`, {
      signal,
      timeout: 15000,
      ...(code ? { params: { code } } : {}),
    })
  );
  if (!Array.isArray(data) || data.some((r) => !r?.id || typeof r.fecha !== "string")) {
    throw new Error("La respuesta del histórico tiene un formato inválido.");
  }
  return data;
}

export async function crearLiquidacionInfoConcesion(liquidacion) {
  const data = obtenerDatos(
    await axiosInstance.post(`${baseURL()}/liquidaciones`, liquidacion, { timeout: 30000 })
  );
  if (!data?.id)
    throw new Error(
      "El servidor no confirmó el registro de la liquidación. Revisa el histórico antes de reintentar."
    );
  return data;
}

// The commerce catalog is available even while the liquidations backend is pending.
export async function getConcesionPorCodigoInfoConcesion(code, signal) {
  if (!code) throw new Error("Tu usuario no tiene un código de concesión asignado.");
  const { data } = await axiosInstance.get("/api/commerce/get-all", { signal, timeout: 15000 });
  if (!Array.isArray(data))
    throw new Error("El catálogo de concesiones tiene un formato inválido.");
  const matches = data.filter(
    (row) => row?.type === "PEAJE" && String(row.code ?? "").trim() === code
  );
  if (matches.length !== 1 || !matches[0].id || !matches[0].name) {
    throw new Error("El código de tu usuario no identifica una concesión de tipo PEAJE válida.");
  }
  return { id: matches[0].id, code, nombre: matches[0].name };
}

// Use the existing users endpoint when login omits the persisted account code.
// Only the current account's scalar code leaves this function.
export async function getCodigoUsuarioInfoConcesion({ id, username }, signal) {
  if (!id && !username) throw new Error("No se pudo identificar al usuario de la sesión.");
  const { data } = await axiosInstance.get("/api/users/all", {
    signal,
    timeout: 15000,
    params: id ? { id } : { username },
  });
  const users = typeof data === "string" ? JSON.parse(data) : data;
  if (!Array.isArray(users)) throw new Error("La respuesta de usuarios tiene un formato inválido.");
  const matches = users.filter(
    (row) =>
      row && (id ? String(row.id) === String(id) : String(row.username || "").trim() === username)
  );
  if (matches.length !== 1)
    throw new Error("No se encontró un único registro para el usuario de la sesión.");
  const code = codigoConcesionUsuario({ more: matches[0].more });
  if (!code) throw new Error("Tu usuario no tiene un código de concesión asignado.");
  return code;
}

// Toll concessions available to users with access to Información de concesión.
export async function getComerciosConsultaInfoConcesion(signal) {
  const { data } = await axiosInstance.get("/api/commerce/get-all", { signal, timeout: 15000 });
  if (!Array.isArray(data)) throw new Error("El catálogo de comercios tiene un formato inválido.");
  const comerciosValidos = data.filter(
    (row) =>
      row?.type === "PEAJE" &&
      row.id &&
      String(row.code ?? "").trim() &&
      String(row.name ?? "").trim()
  );
  const counts = new Map();
  comerciosValidos.forEach((row) => {
    const code = String(row.code).trim();
    counts.set(code, (counts.get(code) || 0) + 1);
  });
  return comerciosValidos
    .filter((row) => counts.get(String(row.code).trim()) === 1)
    .map((row) => ({ id: row.id, code: String(row.code).trim(), nombre: String(row.name).trim() }))
    .sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));
}
